import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../api/errors";
import { putToSignedUrl } from "../api/images";
import { productsApi } from "../api/products";
import type { ColorVariant } from "../api/types";
import { uploadsApi, type SignedUpload } from "../api/uploads";
import { normalizeColorFolderName, type PaletteColor } from "../catalog/colors";
import { categoryFor, type Audience, type FrameShape, type GlassesType } from "../catalog/taxonomy";
import { parseStock, pricesFor, type GlassesDetails } from "./glassesDetails";
import { ROLE_LABELS, type ParsedGlassesFolder, type SourceRole } from "./glassesFolderParser";

/**
 * Turning a reviewed folder into a real draft product, one step at a time.
 *
 * The order below is forced by the backend and cannot be rearranged: creating a
 * product takes the id of an upload batch the worker has already finished, so
 * the bytes must be in object storage and processed before there is anything to
 * create a product from.
 *
 *   initiate -> PUT each file straight to storage -> complete -> worker
 *   -> create product -> describe the draft -> name and stock each colour
 *
 * Image bytes never pass through the backend. The signed URLs live in a ref for
 * the lifetime of the import and nowhere else: not in the query cache, not in
 * component state that could be serialised into a devtools snapshot, not in any
 * message shown to an administrator, and never in a log line.
 */

/** The states the operator is shown. Every one is a real thing that happened.
 *
 * queued and running are deliberately distinct: "waiting for a worker to pick
 * this up" and "a worker is building the images" are different facts, and
 * collapsing them into one "Processing" would tell the operator a job has
 * started when it has not. `unavailable` is recoverable — the photographs are
 * safe and processing is simply down for the moment. */
export type ImportPhase =
  | "idle"
  | "preparing"
  | "uploading"
  | "queued"
  | "running"
  | "creating"
  | "review_needed"
  | "complete"
  | "failed"
  | "unavailable"
  | "cancelled";

export const PHASE_LABELS: Record<ImportPhase, string> = {
  idle: "Ready",
  preparing: "Preparing import",
  uploading: "Uploading files",
  queued: "Waiting for image preparation",
  running: "Preparing product images",
  creating: "Creating the product",
  review_needed: "Review needed",
  complete: "Complete",
  failed: "Failed",
  unavailable: "Temporarily unavailable",
  cancelled: "Cancelled",
};

export interface FileProgress {
  /** Identity of one photograph, in the operator's terms. */
  colorFolder: string;
  colorName: string;
  role: SourceRole;
  fileName: string;
  relativePath: string;
  status: "pending" | "sending" | "sent" | "failed";
}

export interface ImportState {
  phase: ImportPhase;
  files: FileProgress[];
  /** Set once the product exists, so the page can show its derivatives. */
  productId: string | null;
  /** A sentence for the administrator. Never carries a URL or an object key. */
  message: string | null;
  warnings: string[];
  /** Real seconds the job has sat queued, measured from when the wait began.
   *  Null unless the phase is `queued`. There is deliberately no percentage —
   *  the worker reports a state, not a fraction, and a bar on a timer is a
   *  picture of nothing. */
  waitingSeconds: number | null;
}

const IDLE: ImportState = {
  phase: "idle",
  files: [],
  productId: null,
  message: null,
  warnings: [],
  waitingSeconds: null,
};

/** How often a running job is asked about. Slow enough not to hammer the API,
 *  fast enough that a short job does not look stuck. */
const POLL_MS = 2000;

export interface ImportInput {
  parse: ParsedGlassesFolder;
  /** Colour folder name -> the palette colour the operator settled on. */
  assignments: Record<string, PaletteColor>;
  details: GlassesDetails;
  audience: Audience;
  glassesType: GlassesType;
  frameShape: FrameShape;
  /**
   * The product the operator CONFIRMED this is another size of, if any.
   *
   * A UUID, never a name. The dashboard may use the name to find a candidate
   * and put it in front of a person (see import/modelCandidate.ts), but the
   * relationship itself is only ever made from a product they said yes to, and
   * the backend resolves it to that model's root.
   */
  modelOfProductId?: string | null;
}

function operatorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.userMessage;
  return fallback;
}

/** The backend's stable, machine-readable code for "no worker can build images
 *  right now". Matched here so the recoverable case is told apart from a real
 *  failure, without reading a status number scattered through the flow. */
function isProcessingUnavailable(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 503 &&
    error.code === "processing_unavailable"
  );
}

/** Shown when processing is down before anything was uploaded. */
const START_UNAVAILABLE_MESSAGE =
  "Image preparation is temporarily unavailable. No import was started. " +
  "Please try again shortly.";

/** Shown when the photographs are already stored but processing is down: the
 *  reassurance that nothing was lost is the part that matters to the operator. */
const UPLOADED_UNAVAILABLE_MESSAGE =
  "Image preparation is temporarily unavailable. Your photographs are safe — " +
  "nothing was lost. Please try again shortly.";

/**
 * The opening quantity for each colour the folder carries, keyed by colour.
 *
 * Sent WITH the creation call, and that is the whole point. A colour's stock is
 * answered exactly once, when the sellable thing first exists: the backend hands
 * the number to InventoryService.create_item, which opens the stable stock item
 * and writes its opening_balance movement in the same call, so there is no
 * moment at which stock exists unexplained. Every later change is a movement
 * through Inventory.
 *
 * It cannot be done afterwards. update_variant has no stock field on purpose,
 * and Inventory's adjust is a delta against a quantity the operator was already
 * looking at — using it here would file units the shop never received as a
 * manual adjustment and bypass the opening balance the ledger is owed.
 *
 * Keyed by the colour folder name; the backend folds both sides through
 * normalize_color_key, so trailing spaces and separators cannot miss.
 */
function openingStock(input: ImportInput): Record<string, number> {
  const opening: Record<string, number> = {};
  for (const color of input.parse.colors) {
    // Blank never reaches here — the details form requires a number per colour
    // before Review — but a blank must still open at zero rather than guess.
    opening[color.folderName] =
      parseStock(input.details.stock[color.folderName] ?? "") ?? 0;
  }
  return opening;
}

export function useGlassesImport() {
  const [state, setState] = useState<ImportState>(IDLE);
  const queryClient = useQueryClient();

  // Signed slots, keyed by the path they were issued for. A ref because this
  // is a secret with a lifetime, not state anything renders.
  const signedRef = useRef<Map<string, SignedUpload>>(new Map());
  const batchRef = useRef<string | null>(null);
  // The processing job, kept so an interrupted-by-unavailability import can be
  // spoken about without creating a second job or re-uploading anything.
  const jobRef = useRef<string | null>(null);
  // Guards double submission: one press of Start Import, one product.
  const runningRef = useRef(false);
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      // Leaving the page stops the polling loop; a job that outlives the screen
      // is still the backend's business, but nothing here keeps asking.
      liveRef.current = false;
    };
  }, []);

  const patch = useCallback((next: Partial<ImportState>) => {
    if (!liveRef.current) return;
    setState((current) => ({ ...current, ...next }));
  }, []);

  const markFile = useCallback(
    (relativePath: string, status: FileProgress["status"]) => {
      if (!liveRef.current) return;
      setState((current) => ({
        ...current,
        files: current.files.map((file) =>
          file.relativePath === relativePath ? { ...file, status } : file,
        ),
      }));
    },
    [],
  );

  /** PUT every file that is not already in storage. Returns the ones that failed. */
  const sendFiles = useCallback(
    async (pending: FileProgress[], byPath: Map<string, File>): Promise<FileProgress[]> => {
      const failed: FileProgress[] = [];
      for (const file of pending) {
        const signed = signedRef.current.get(file.relativePath);
        const blob = byPath.get(file.relativePath);
        if (!signed || !blob) {
          failed.push(file);
          markFile(file.relativePath, "failed");
          continue;
        }
        markFile(file.relativePath, "sending");
        try {
          await putToSignedUrl(signed, blob);
          markFile(file.relativePath, "sent");
        } catch {
          // The thrown error is deliberately not kept: it can carry the signed
          // URL, and this one is reported by colour, view and file name.
          failed.push(file);
          markFile(file.relativePath, "failed");
        }
      }
      return failed;
    },
    [markFile],
  );

  /**
   * Follow the job until it reaches a state that will not change on its own.
   *
   * The states are mapped honestly, never flattened: `queued` means no worker
   * has picked the job up yet and `running` means one is building the images —
   * different facts, shown differently. While the job is merely queued, the
   * availability of a worker is checked; if processing has gone away, the wait
   * is not dressed up as progress — it stops and reports the recoverable
   * `unavailable` outcome, keeping the job id so nothing is re-uploaded or
   * queued a second time. There is no artificial timeout: the project defines
   * none, so none is invented here.
   */
  const awaitJob = useCallback(
    async (
      jobId: string,
    ): Promise<"completed" | "needs_review" | "cancelled" | "unavailable"> => {
      const queuedSince = Date.now();
      for (;;) {
        // Leaving the page stops the loop; the job is still the backend's
        // business, but nothing here keeps asking or pretends it is running.
        if (!liveRef.current) return "unavailable";

        const job = await uploadsApi.job(jobId);

        if (job.status === "completed") return "completed";
        if (job.status === "needs_review") {
          patch({ warnings: job.report?.warnings ?? [] });
          return "needs_review";
        }
        if (job.status === "cancelled") {
          patch({ warnings: job.report?.warnings ?? [] });
          return "cancelled";
        }
        if (job.status === "failed") {
          const errors = job.report?.errors ?? [];
          patch({ warnings: job.report?.warnings ?? [] });
          throw new Error(
            errors[0] ?? "the photographs could not be processed",
          );
        }

        if (job.status === "running") {
          // A worker has it: real progress, no availability doubt.
          patch({ phase: "running", waitingSeconds: null });
        } else {
          // queued: still waiting for a worker. A queue with none is not
          // progress — confirm one exists before calling this a wait.
          let stillAvailable = true;
          try {
            stillAvailable = (await uploadsApi.availability()).available;
          } catch {
            // A failed availability read is not proof of unavailability; keep
            // waiting rather than abandon a job that may be perfectly fine.
            stillAvailable = true;
          }
          if (!stillAvailable) return "unavailable";
          patch({
            phase: "queued",
            waitingSeconds: Math.max(
              0,
              Math.floor((Date.now() - queuedSince) / 1000),
            ),
          });
        }

        await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      }
    },
    [patch],
  );

  /**
   * Name, stock and size every colour the worker produced.
   *
   * The worker names a variant after the folder it came from; this replaces
   * that with the palette colour the administrator actually chose, which is the
   * name and swatch a customer will see.
   */
  const describeVariants = useCallback(
    async (
      productId: string,
      variants: readonly ColorVariant[],
      input: ImportInput,
    ): Promise<void> => {
      for (const variant of variants) {
        const folder = input.parse.colors.find(
          (color) =>
            normalizeColorFolderName(color.folderName) ===
            normalizeColorFolderName(variant.normalized_color_key),
        );
        if (!folder) continue;

        const chosen =
          folder.color.kind === "matched"
            ? folder.color.color
            : input.assignments[folder.folderName];
        if (!chosen) continue;

        // The draft's version is read again before each colour rather than
        // counted forward. A write that changes nothing does not bump it, so
        // an assumed +1 would send a version the server rejects — and the one
        // thing this loop must not do is fail halfway and leave half the
        // colours named after their folders.
        const current = await productsApi.detail(productId);
        const expected = current.draft_revision?.version;
        if (expected === undefined) return;

        // No stock here, and its absence is the contract. UpdateVariantRequest
        // forbids the field outright — sending it answered 422 and failed the
        // whole import after the photographs were already stored. Stock reaches
        // a colour once, as an opening quantity at creation (see openingStock),
        // and every later change is a movement through Inventory.
        // No size here either, and its absence is the same kind of contract as
        // stock's above. A frame's size belongs to the PRODUCT ENTRY — the one
        // the shopper buys, with its own code and its own stock — not to each
        // colour of it. Writing it per colour is what let one entry claim two
        // sizes and say "Small · Medium" on a product page.
        await productsApi.updateVariant(productId, variant.id, {
          expected_version: expected,
          name: chosen.name,
          swatch_hex: chosen.swatch_hex,
        });
      }
    },
    [],
  );

  const start = useCallback(
    async (input: ImportInput) => {
      if (runningRef.current) return;
      runningRef.current = true;

      const byPath = new Map<string, File>();
      const files: FileProgress[] = [];
      for (const color of input.parse.colors) {
        const chosen =
          color.color.kind === "matched"
            ? color.color.color
            : input.assignments[color.folderName];
        for (const source of color.sources) {
          byPath.set(source.file.relativePath, source.file.file);
          files.push({
            colorFolder: color.folderName,
            colorName: chosen?.name ?? color.folderName,
            role: source.role,
            fileName: source.file.name,
            relativePath: source.file.relativePath,
            status: "pending",
          });
        }
      }

      setState({
        phase: "preparing",
        files,
        productId: null,
        message: null,
        warnings: [],
        waitingSeconds: null,
      });

      // Whether the photographs reached storage, so a later unavailability can
      // reassure the operator that nothing was lost.
      let uploadedOk = false;
      try {
        const initiated = await uploadsApi.initiate({
          original_filename_or_folder: input.parse.rootName,
          relative_paths: files.map((file) => file.relativePath),
        });
        batchRef.current = initiated.upload_batch_id;
        signedRef.current = new Map(
          files.map((file, index) => [file.relativePath, initiated.files[index]!]),
        );

        patch({ phase: "uploading" });
        const failed = await sendFiles(files, byPath);
        if (failed.length > 0) {
          runningRef.current = false;
          patch({
            phase: "failed",
            message:
              failed.length === 1
                ? `The ${ROLE_LABELS[failed[0]!.role]} photograph for ` +
                  `${failed[0]!.colorName} did not reach storage.`
                : `${failed.length} photographs did not reach storage.`,
          });
          return;
        }
        uploadedOk = true;

        // Hand the finished batch over. The backend re-checks that a worker is
        // there before it creates a job; if not, this raises 503 and is caught
        // below as the recoverable `unavailable` case, with no job created.
        const completed = await uploadsApi.complete(initiated.upload_batch_id);
        jobRef.current = completed.processing_job_id;
        if (!completed.queued) {
          // The job row exists but could not be queued (a Redis hiccup). The
          // files are safe; this is recoverable, not a failed import.
          runningRef.current = false;
          patch({
            phase: "unavailable",
            waitingSeconds: null,
            message: UPLOADED_UNAVAILABLE_MESSAGE,
          });
          return;
        }

        patch({ phase: "queued", waitingSeconds: 0 });
        const outcome = await awaitJob(completed.processing_job_id);
        if (outcome === "unavailable") {
          // Queued, then the worker went away. Keep the job id, re-upload
          // nothing and queue nothing again — just tell the truth.
          runningRef.current = false;
          patch({
            phase: "unavailable",
            waitingSeconds: null,
            message: UPLOADED_UNAVAILABLE_MESSAGE,
          });
          return;
        }
        if (outcome === "cancelled") {
          runningRef.current = false;
          patch({
            phase: "cancelled",
            waitingSeconds: null,
            message: "The import was cancelled. Nothing has been published.",
          });
          return;
        }
        if (outcome === "needs_review") {
          runningRef.current = false;
          patch({
            phase: "review_needed",
            waitingSeconds: null,
            message:
              "The photographs were processed but need a look before this can " +
              "become a product.",
          });
          return;
        }

        patch({ phase: "creating", waitingSeconds: null });
        const { base, sale } = pricesFor(input.details.pricing);
        const created = await productsApi.create({
          upload_batch_id: initiated.upload_batch_id,
          name: input.details.name.trim(),
          product_code: input.details.productCode.trim(),
          base_price: base ?? 0,
          sale_price: sale,
          currency: "ILS",
          category: categoryFor("glasses", input.glassesType),
          frame_shape: input.frameShape,
          audience: input.audience,
          // The ONE size this entry is, on the product where identity lives.
          frame_size: input.details.size,
          model_of_product_id: input.modelOfProductId ?? null,
          initial_stock: openingStock(input),
        });

        const productId = created.product.id;
        // The description is not part of creation, so it is the first edit the
        // new draft receives.
        await productsApi.updateDraft(productId, {
          expected_version: created.draft_revision.version,
          description: input.details.description.trim(),
        });

        await describeVariants(
          productId,
          created.draft_revision.color_variants ?? [],
          input,
        );

        await queryClient.invalidateQueries({ queryKey: ["products"] });
        runningRef.current = false;
        patch({ phase: "complete", productId });
      } catch (error) {
        runningRef.current = false;
        if (isProcessingUnavailable(error)) {
          // The backend refused to start or continue processing. Recoverable:
          // any photographs that reached storage are still there.
          patch({
            phase: "unavailable",
            waitingSeconds: null,
            message: uploadedOk
              ? UPLOADED_UNAVAILABLE_MESSAGE
              : START_UNAVAILABLE_MESSAGE,
          });
          return;
        }
        patch({
          phase: "failed",
          waitingSeconds: null,
          message: operatorMessage(
            error,
            error instanceof Error && error.message
              ? error.message
              : "The import could not be finished.",
          ),
        });
      }
    },
    [awaitJob, describeVariants, patch, queryClient, sendFiles],
  );

  /**
   * Send one photograph again, and only that one.
   *
   * The files already in storage stay there, the batch stays the same, and no
   * second product is created — this picks the import back up from the file
   * that failed.
   */
  const retryFile = useCallback(
    async (relativePath: string, blob: File) => {
      const signed = signedRef.current.get(relativePath);
      if (!signed || runningRef.current) return;
      markFile(relativePath, "sending");
      try {
        await putToSignedUrl(signed, blob);
        markFile(relativePath, "sent");
      } catch {
        markFile(relativePath, "failed");
      }
    },
    [markFile],
  );

  const reset = useCallback(() => {
    runningRef.current = false;
    signedRef.current = new Map();
    batchRef.current = null;
    jobRef.current = null;
    setState(IDLE);
  }, []);

  return { state, start, retryFile, reset };
}
